import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import MLUrgencyPanel from '../components/referral/MLUrgencyPanel';
import NIRImagingPanel from '../components/referral/NIRImagingPanel';
import { 
    ArrowLeft, Download, User, Calculator, Activity, 
    HeartPulse, Pill, Stethoscope, ClipboardList, FileText,
    CheckCircle, XCircle, AlertTriangle, Clock, TrendingUp,
    FileCheck, Send, Bot, Sparkles, Copy
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = '/logo.png';

// Helper function to calculate age from DOB
const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const dob = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--;
    }
    return age;
};

const urgencyColors = {
    urgent: 'bg-rose-100 text-rose-700 border-rose-200',
    semi_urgent: 'bg-amber-100 text-amber-700 border-amber-200',
    moderate: 'bg-amber-100 text-amber-700 border-amber-200', // Keep for backward compatibility
    routine: 'bg-slate-100 text-slate-700 border-slate-200',
    screening_rejected: 'bg-red-100 text-red-700 border-red-200'
};

const statusColors = {
    pending: 'bg-amber-100 text-amber-800 border-amber-200',
    accepted: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-100 text-red-700 border-red-200',
    screening_rejected: 'bg-red-100 text-red-700 border-red-200',
    scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
    completed: 'bg-slate-100 text-slate-700 border-slate-200'
};

const conditionLabels = {
    hip_arthritis: 'Hip Osteoarthritis',
    knee_arthritis: 'Knee Osteoarthritis',
    shoulder_arthritis: 'Shoulder Osteoarthritis',
    rotator_cuff_tear: 'Rotator Cuff Tear',
    impingement_syndrome: 'Impingement Syndrome',
    calcific_tendinitis: 'Calcific Tendinitis',
    ankle_arthritis: 'Ankle Osteoarthritis',
    ankle_impingement: 'Ankle Impingement',
    hallux_valgus: 'Hallux Valgus (Bunion)',
    spinal_stenosis: 'Spinal Stenosis',
    disc_herniation: 'Disc Herniation',
    carpal_tunnel_syndrome: 'Carpal Tunnel Syndrome',
    trigger_finger: 'Trigger Finger/Thumb',
    dupuytrens_contracture: "Dupuytren's Contracture",
    base_thumb_arthritis: 'Base of Thumb Arthritis',
    tennis_elbow: 'Tennis Elbow',
    golfers_elbow: "Golfer's Elbow",
    ulnar_nerve_compression: 'Ulnar Nerve Compression'
};

export default function ReferralDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isConsultant } = useAuth();
    const [referral, setReferral] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reviewing, setReviewing] = useState(false);
    const [reviewNotes, setReviewNotes] = useState('');
    const [urgencyOverride, setUrgencyOverride] = useState('');
    const [preClinicNote, setPreClinicNote] = useState(null);
    const [aiSummary, setAiSummary] = useState(null);
    const [aiAnalysis, setAiAnalysis] = useState(null);
    const [aiLoading, setAiLoading] = useState(false);
    const [showPreClinicNote, setShowPreClinicNote] = useState(false);
    const contentRef = useRef(null);

    useEffect(() => {
        fetchReferral();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const fetchReferral = async () => {
        try {
            const response = await axios.get(`${API}/referrals/${id}`);
            setReferral(response.data);
        } catch (error) {
            toast.error('Failed to load referral');
            navigate('/dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleReview = async (action) => {
        setReviewing(true);
        try {
            const response = await axios.put(`${API}/referrals/${id}/review`, {
                action,
                notes: reviewNotes,
                urgency_override: urgencyOverride || null
            });
            setReferral(response.data);
            toast.success(`Referral ${action}ed successfully`);
        } catch (error) {
            toast.error('Failed to submit review');
        } finally {
            setReviewing(false);
        }
    };

    const generatePDF = () => {
        if (!referral) return;

        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        let yPos = 20;
        const lineHeight = 7;
        const margin = 20;

        // Header
        doc.setFontSize(20);
        doc.setTextColor(15, 118, 110); // Teal
        doc.text('FendanaCor Referral Report', margin, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Generated: ${new Date().toLocaleString('en-GB')}`, margin, yPos);
        doc.text(`Referral ID: ${referral.id}`, pageWidth - margin - 60, yPos);
        yPos += 15;

        // Status Banner
        if (referral.screening_passed) {
            doc.setFillColor(16, 185, 129);
        } else {
            doc.setFillColor(239, 68, 68);
        }
        doc.rect(margin, yPos, pageWidth - 2 * margin, 10, 'F');
        doc.setTextColor(255);
        doc.setFontSize(12);
        doc.text(
            referral.screening_passed ? 'REFERRAL ACCEPTED' : 'SCREENING NOT PASSED',
            pageWidth / 2,
            yPos + 7,
            { align: 'center' }
        );
        yPos += 20;

        // Patient Details
        doc.setTextColor(0);
        doc.setFontSize(14);
        doc.text('Patient Details', margin, yPos);
        yPos += lineHeight;
        
        doc.setFontSize(10);
        doc.text(`Name: ${referral.patient.first_name} ${referral.patient.last_name}`, margin, yPos);
        yPos += lineHeight;
        doc.text(`DOB: ${referral.patient.date_of_birth} (Age: ${referral.patient_age || calculateAge(referral.patient.date_of_birth)} years)`, margin, yPos);
        doc.text(`NHS: ${referral.patient.nhs_number || 'Not provided'}`, margin + 80, yPos);
        yPos += lineHeight;
        doc.text(`Phone: ${referral.patient.phone || 'Not provided'}`, margin, yPos);
        yPos += 12;

        // Clinical Summary
        doc.setFontSize(14);
        doc.text('Clinical Summary', margin, yPos);
        yPos += lineHeight;
        
        doc.setFontSize(10);
        doc.text(`Condition: ${conditionLabels[referral.condition] || referral.condition || 'Unknown'}`, margin, yPos);
        yPos += lineHeight;
        doc.text(`Body Part: ${referral.body_part || 'unknown'} (${referral.clinical_findings?.affected_side || 'unknown'})`, margin, yPos);
        yPos += lineHeight;
        doc.text(`BMI: ${referral.bmi ?? '—'} (${(referral.bmi_category || '').replace(/_/g, ' ') || '—'})`, margin, yPos);
        yPos += lineHeight;
        doc.text(`Symptom Duration: ${referral.clinical_findings?.symptom_duration_months ?? '—'} months`, margin, yPos);
        yPos += 12;

        // Urgency Assessment
        doc.setFontSize(14);
        doc.text('Urgency Assessment', margin, yPos);
        yPos += lineHeight;
        
        doc.setFontSize(10);
        doc.text(`Urgency Score: ${referral.urgency_score ?? '—'}/100`, margin, yPos);
        doc.text(`Level: ${(referral.urgency_level || 'unknown').replace(/_/g, ' ').toUpperCase()}`, margin + 60, yPos);
        yPos += 12;

        // Guidelines Applied
        doc.setFontSize(14);
        doc.text('Guidelines Applied', margin, yPos);
        yPos += lineHeight;
        
        doc.setFontSize(10);
        referral.guidelines_applied.forEach(g => {
            doc.text(`• ${g}`, margin, yPos);
            yPos += lineHeight;
        });
        yPos += 5;

        // Recommendations
        if (referral.recommendations.length > 0) {
            doc.setFontSize(14);
            doc.text('Recommendations', margin, yPos);
            yPos += lineHeight;
            
            doc.setFontSize(10);
            referral.recommendations.forEach(rec => {
                const lines = doc.splitTextToSize(`• ${rec}`, pageWidth - 2 * margin);
                lines.forEach(line => {
                    if (yPos > 270) {
                        doc.addPage();
                        yPos = 20;
                    }
                    doc.text(line, margin, yPos);
                    yPos += lineHeight;
                });
            });
        }

        // Screening Results
        if (!referral.screening_passed && referral.screening_report.failures.length > 0) {
            if (yPos > 240) {
                doc.addPage();
                yPos = 20;
            }
            
            doc.setFontSize(14);
            doc.setTextColor(220, 38, 38);
            doc.text('Screening Criteria Not Met', margin, yPos);
            yPos += lineHeight;
            
            doc.setFontSize(10);
            doc.setTextColor(0);
            referral.screening_report.failures.forEach(failure => {
                const lines = doc.splitTextToSize(`• ${failure.criterion}: ${failure.detail}`, pageWidth - 2 * margin);
                lines.forEach(line => {
                    if (yPos > 270) {
                        doc.addPage();
                        yPos = 20;
                    }
                    doc.text(line, margin, yPos);
                    yPos += lineHeight;
                });
            });
        }

        // Footer
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
            'This report is generated by FendanaCor Orthopedic Referral Triage System',
            pageWidth / 2,
            285,
            { align: 'center' }
        );

        doc.save(`referral_${referral.id.slice(0, 8)}.pdf`);
        toast.success('PDF downloaded successfully');
    };

    const fetchPreClinicNote = async () => {
        setAiLoading(true);
        try {
            const response = await axios.get(`${API}/referrals/${id}/pre-clinic-note`);
            setPreClinicNote(response.data);
            setShowPreClinicNote(true);
        } catch (error) {
            toast.error('Failed to generate pre-clinic note');
        } finally {
            setAiLoading(false);
        }
    };

    const generateAISummary = async () => {
        setAiLoading(true);
        try {
            const response = await axios.post(`${API}/ai/clinical-summary/${id}`);
            if (response.data.success) {
                setAiSummary(response.data.summary);
                toast.success('AI summary generated');
            } else {
                toast.error(response.data.error || 'Failed to generate summary');
            }
        } catch (error) {
            toast.error('Failed to generate AI summary');
        } finally {
            setAiLoading(false);
        }
    };

    const generateAIAnalysis = async () => {
        setAiLoading(true);
        try {
            const response = await axios.post(`${API}/ai/analyze/${id}`);
            if (response.data.success) {
                setAiAnalysis(response.data.analysis);
                toast.success('AI analysis generated');
            } else {
                toast.error(response.data.error || 'Failed to analyze');
            }
        } catch (error) {
            toast.error('Failed to generate AI analysis');
        } finally {
            setAiLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center">
                <div className="text-center">
                    <Activity className="w-8 h-8 animate-spin mx-auto mb-4 text-teal-600" />
                    <p className="text-slate-500">Loading referral...</p>
                </div>
            </div>
        );
    }

    if (!referral) {
        return null;
    }

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => navigate('/dashboard')}
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Back to Dashboard
                        </Button>
                        <div className="flex items-center gap-3">
                            <img src={LOGO_URL} alt="FendanaCor" className="w-8 h-8 rounded-lg" />
                            <span className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Referral Details
                            </span>
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={generatePDF}
                            data-testid="download-pdf-btn"
                            className="btn-secondary"
                        >
                            <Download className="w-4 h-4 mr-2" />
                            Export PDF
                        </Button>
                        {isConsultant && (
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={fetchPreClinicNote}
                                disabled={aiLoading}
                                data-testid="pre-clinic-note-btn"
                                className="btn-secondary"
                            >
                                <FileText className="w-4 h-4 mr-2" />
                                Pre-Clinic Note
                            </Button>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" ref={contentRef}>
                {/* Status Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <Card className={`card-clinical overflow-hidden ${
                        referral.screening_passed ? 'border-t-4 border-t-emerald-500' : 'border-t-4 border-t-rose-500'
                    }`}>
                        <CardContent className="pt-6">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-3 mb-2">
                                        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                            {referral.patient?.first_name || '—'} {referral.patient?.last_name || ''}
                                        </h1>
                                        {referral.urgency_level && (
                                            <Badge className={urgencyColors[referral.urgency_level]}>
                                                {referral.urgency_level.replace(/_/g, ' ')}
                                            </Badge>
                                        )}
                                        {referral.status && (
                                            <Badge className={statusColors[referral.status]}>
                                                {referral.status.replace(/_/g, ' ')}
                                            </Badge>
                                        )}
                                    </div>
                                    <p className="text-slate-600">
                                        {conditionLabels[referral.condition] || referral.condition || 'Unknown condition'}
                                        <span className="mx-2">•</span>
                                        {(referral.body_part || 'unknown').charAt(0).toUpperCase() + (referral.body_part || 'unknown').slice(1)}
                                    </p>
                                    <p className="text-sm text-slate-500 mt-1">
                                        Referred by {referral.referring_doctor_name || 'Unknown'} on {formatDate(referral.created_at)}
                                    </p>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="text-center">
                                        <p className="text-xs text-slate-500 mb-1">Urgency Score</p>
                                        <div className={`text-3xl font-bold ${
                                            (referral.urgency_score || 0) >= 80 ? 'text-rose-600' :
                                            (referral.urgency_score || 0) >= 60 ? 'text-amber-600' :
                                            'text-teal-600'
                                        }`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                                            {referral.urgency_score ?? '—'}
                                            <span className="text-lg text-slate-400">/100</span>
                                        </div>
                                    </div>
                                    <div className={`p-3 rounded-full ${
                                        referral.screening_passed ? 'bg-emerald-100' : 'bg-rose-100'
                                    }`}>
                                        {referral.screening_passed ? (
                                            <CheckCircle className="w-8 h-8 text-emerald-600" />
                                        ) : (
                                            <XCircle className="w-8 h-8 text-rose-600" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Patient Details */}
                        <Card className="card-clinical">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    <User className="w-5 h-5 text-teal-600" />
                                    Patient Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <p className="text-slate-500">NHS Number</p>
                                        <p className="font-medium">{referral.patient.nhs_number || '-'}</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-500">Date of Birth</p>
                                        <p className="font-medium">
                                            {referral.patient.date_of_birth}
                                            {referral.patient.date_of_birth && (
                                                <span className="ml-2 text-teal-600 font-semibold">
                                                    ({referral.patient_age || calculateAge(referral.patient.date_of_birth)} years old)
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-slate-500">Gender</p>
                                        <p className="font-medium">{referral.patient.gender}</p>
                                    </div>
                                    <div>
                                        <p className="text-slate-500">Phone</p>
                                        <p className="font-medium">{referral.patient.phone || '-'}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <p className="text-slate-500">Address</p>
                                        <p className="font-medium">
                                            {referral.patient.address || '-'}, {referral.patient.postcode || ''}
                                        </p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* NIR Imaging History (NHS National Imaging Registry) */}
                        <NIRImagingPanel
                            referralId={id}
                            nhsNumber={referral.patient?.nhs_number}
                        />

                        {/* Clinical Summary */}
                        <Card className="card-clinical">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    <Activity className="w-5 h-5 text-teal-600" />
                                    Clinical Summary
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div className="p-3 bg-slate-50 rounded-lg">
                                        <p className="text-slate-500 text-xs">BMI</p>
                                        <p className="font-bold text-lg">{referral.bmi ?? '—'}</p>
                                        <p className="text-xs text-slate-500">{(referral.bmi_category || '').replace(/_/g, ' ') || '—'}</p>
                                    </div>
                                    <div className="p-3 bg-slate-50 rounded-lg">
                                        <p className="text-slate-500 text-xs">Duration</p>
                                        <p className="font-bold text-lg">{referral.clinical_findings?.symptom_duration_months ?? '—'}</p>
                                        <p className="text-xs text-slate-500">months</p>
                                    </div>
                                    <div className="p-3 bg-slate-50 rounded-lg">
                                        <p className="text-slate-500 text-xs">Side</p>
                                        <p className="font-bold text-lg capitalize">{referral.clinical_findings?.affected_side || '—'}</p>
                                    </div>
                                    <div className="p-3 bg-slate-50 rounded-lg">
                                        <p className="text-slate-500 text-xs">Pain Score</p>
                                        <p className="font-bold text-lg">{referral.questionnaire_scores?.vas_pain_score ?? '-'}/10</p>
                                    </div>
                                </div>

                                <Separator />

                                <div className="space-y-2">
                                    <p className="text-sm text-slate-500">Pain Characteristics</p>
                                    <div className="flex gap-3">
                                        {referral.clinical_findings?.pain_at_rest && (
                                            <Badge variant="outline">Pain at rest</Badge>
                                        )}
                                        {referral.clinical_findings?.night_pain && (
                                            <Badge variant="outline">Night pain</Badge>
                                        )}
                                        <Badge variant="outline">
                                            {referral.clinical_findings?.functional_limitation || 'unknown'} limitation
                                        </Badge>
                                    </div>
                                </div>

                                {referral.clinical_findings?.xray_findings && (
                                    <div>
                                        <p className="text-sm text-slate-500 mb-1">X-Ray Findings</p>
                                        <p className="text-sm">{referral.clinical_findings.xray_findings}</p>
                                    </div>
                                )}

                                {referral.clinical_notes && (
                                    <div>
                                        <p className="text-sm text-slate-500 mb-1">Clinical Notes</p>
                                        <p className="text-sm">{referral.clinical_notes}</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Treatment History */}
                        <Card className="card-clinical">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    <Stethoscope className="w-5 h-5 text-teal-600" />
                                    Treatment History
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                        <span className="text-sm">Physiotherapy</span>
                                        <span className="font-medium">
                                            {referral.treatment_history?.physiotherapy
                                                ? `${referral.treatment_history?.physio_sessions ?? 0} sessions`
                                                : 'No'}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                        <span className="text-sm">Regular Analgesia</span>
                                        <span className="font-medium">
                                            {referral.treatment_history?.regular_analgesia
                                                ? `${referral.treatment_history?.analgesia_duration_months ?? 0} months`
                                                : 'No'}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                        <span className="text-sm">Injections</span>
                                        <span className="font-medium">
                                            {referral.treatment_history?.intra_articular_injections
                                                ? `${referral.treatment_history?.injection_count ?? 0}x ${referral.treatment_history?.injection_type || ''}`
                                                : 'No'}
                                        </span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Screening Result */}
                        <Card className={`card-clinical ${
                            referral.screening_passed ? 'bg-emerald-50/50 border-emerald-200' : 'bg-rose-50/50 border-rose-200'
                        }`}>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    <FileCheck className="w-5 h-5" />
                                    Screening Result
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center gap-2">
                                    {referral.screening_passed ? (
                                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                                    ) : (
                                        <XCircle className="w-5 h-5 text-rose-600" />
                                    )}
                                    <span className={`font-semibold ${
                                        referral.screening_passed ? 'text-emerald-700' : 'text-rose-700'
                                    }`}>
                                        {referral.screening_passed ? 'Criteria Met' : 'Criteria Not Met'}
                                    </span>
                                </div>

                                <div>
                                    <p className="text-xs text-slate-500 mb-2">Guidelines Applied</p>
                                    <div className="flex flex-wrap gap-1">
                                        {(referral.guidelines_applied || []).map((g, i) => (
                                            <Badge key={`guideline-${i}-${g}`} variant="outline" className="text-xs">
                                                {g}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>

                                {/* Failures */}
                                {!referral.screening_passed && (referral.screening_report?.failures || []).length > 0 && (
                                    <div className="space-y-2">
                                        <p className="text-xs text-rose-600 font-medium">Issues Found:</p>
                                        {(referral.screening_report?.failures || []).map((f, i) => (
                                            <div key={`failure-${i}-${f?.criterion || ''}`} className="p-2 bg-white rounded text-xs">
                                                <p className="font-medium text-rose-700">{f.criterion}</p>
                                                <p className="text-rose-600">{f.detail}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Recommendations */}
                        {(referral.recommendations || []).length > 0 && (
                            <Card className="card-clinical">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                                        Recommendations
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <ul className="space-y-2">
                                        {(referral.recommendations || []).map((rec, i) => (
                                            <li key={`rec-${i}-${(rec || '').slice(0, 12)}`} className="text-sm text-slate-700 flex items-start gap-2">
                                                <span className="text-amber-500 mt-1">•</span>
                                                {rec}
                                            </li>
                                        ))}
                                    </ul>
                                </CardContent>
                            </Card>
                        )}

                        {/* Consultant Review Panel */}
                        {isConsultant && referral.status === 'pending' && (
                            <Card className="card-clinical border-teal-200 bg-teal-50/30">
                                <CardHeader>
                                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Consultant Review
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <MLUrgencyPanel
                                        referralId={referral.id}
                                        currentUrgency={referral.urgency_level}
                                        overrideValue={urgencyOverride}
                                        onApplySuggestion={(s) => setUrgencyOverride(s)}
                                    />
                                    <div className="space-y-2">
                                        <Label className="label-clinical">Override Urgency Level (Optional)</Label>
                                        <Select 
                                            value={urgencyOverride} 
                                            onValueChange={setUrgencyOverride}
                                        >
                                            <SelectTrigger data-testid="urgency-override-select">
                                                <SelectValue placeholder="Keep current level" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="urgent">Urgent</SelectItem>
                                                <SelectItem value="semi_urgent">Semi-urgent</SelectItem>
                                                <SelectItem value="routine">Routine</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="label-clinical">Review Notes</Label>
                                        <Textarea
                                            value={reviewNotes}
                                            onChange={(e) => setReviewNotes(e.target.value)}
                                            placeholder="Add any notes for this referral..."
                                            className="input-clinical"
                                            data-testid="review-notes-textarea"
                                        />
                                    </div>

                                    <div className="flex gap-2">
                                        <Button
                                            onClick={() => handleReview('accept')}
                                            disabled={reviewing}
                                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                            data-testid="accept-referral-btn"
                                        >
                                            <CheckCircle className="w-4 h-4 mr-2" />
                                            Accept
                                        </Button>
                                        <Button
                                            onClick={() => handleReview('reject')}
                                            disabled={reviewing}
                                            variant="destructive"
                                            className="flex-1"
                                            data-testid="reject-referral-btn"
                                        >
                                            <XCircle className="w-4 h-4 mr-2" />
                                            Reject
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Consultant Notes (if reviewed) */}
                        {referral.consultant_notes && (
                            <Card className="card-clinical">
                                <CardHeader>
                                    <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        Consultant Notes
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-slate-700">{referral.consultant_notes}</p>
                                </CardContent>
                            </Card>
                        )}

                        {/* AI Tools Panel */}
                        {isConsultant && (
                            <Card className="card-clinical border-purple-200 bg-purple-50/30">
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                        <Bot className="w-5 h-5 text-purple-600" />
                                        AI Assistant
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex flex-wrap gap-2">
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            onClick={generateAISummary}
                                            disabled={aiLoading}
                                            data-testid="ai-summary-btn"
                                        >
                                            <Sparkles className="w-4 h-4 mr-2" />
                                            Generate AI Summary
                                        </Button>
                                        <Button 
                                            variant="outline" 
                                            size="sm" 
                                            onClick={generateAIAnalysis}
                                            disabled={aiLoading}
                                            data-testid="ai-analysis-btn"
                                        >
                                            <Bot className="w-4 h-4 mr-2" />
                                            AI Screening Analysis
                                        </Button>
                                    </div>

                                    {aiSummary && (
                                        <div className="p-4 bg-white rounded-lg border">
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="font-semibold text-sm">AI Clinical Summary</h4>
                                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(aiSummary)}>
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                            </div>
                                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{aiSummary}</p>
                                        </div>
                                    )}

                                    {aiAnalysis && (
                                        <div className="p-4 bg-white rounded-lg border">
                                            <div className="flex justify-between items-center mb-2">
                                                <h4 className="font-semibold text-sm">AI Screening Analysis</h4>
                                                <Button variant="ghost" size="sm" onClick={() => copyToClipboard(aiAnalysis)}>
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                            </div>
                                            <p className="text-sm text-slate-700 whitespace-pre-wrap">{aiAnalysis}</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </main>

            {/* Pre-Clinic Note Modal */}
            {showPreClinicNote && preClinicNote && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col bg-white">
                        <CardHeader className="flex-shrink-0 border-b">
                            <div className="flex items-center justify-between">
                                <CardTitle className="flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-teal-600" />
                                    Pre-Clinic Note - {preClinicNote.patient_name}
                                </CardTitle>
                                <Button variant="ghost" size="sm" onClick={() => setShowPreClinicNote(false)}>
                                    <XCircle className="w-5 h-5" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="flex-1 overflow-y-auto p-6">
                            <pre className="whitespace-pre-wrap font-mono text-sm bg-slate-50 p-4 rounded-lg">
                                {preClinicNote.formatted_note}
                            </pre>
                        </CardContent>
                        <div className="flex-shrink-0 border-t p-4 bg-slate-50 flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setShowPreClinicNote(false)}>Close</Button>
                            <Button onClick={() => copyToClipboard(preClinicNote.formatted_note)} className="bg-teal-600 hover:bg-teal-700">
                                <Copy className="w-4 h-4 mr-2" />
                                Copy Note
                            </Button>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}
